import { useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { useToast } from '@mustauto/ui/web';
import { formatApiError, isValidPlate, maskPlate, normalizePlate } from '@mustauto/utils';
import { CatalogAutocomplete, type CatalogItem } from '@/components/CatalogAutocomplete';

export interface VehicleCorrection {
  plate: string;
  make: string;
  model: string;
  yearModel: number;
}

export type VehicleCorrectionOutcome = 'unchanged' | 'renamed' | 'moved' | 'created' | 'edited';

const INPUT = 'mt-1 block h-9 w-full rounded-md border border-input bg-background px-3 text-sm';

/**
 * Placa, veículo e ano no card da OS, com a correção.
 *
 * A placa só era gravada na abertura: digitada errada (ou lida errada pelo
 * OCR), a oficina pedia ao suporte. A regra de o que muda no cadastro do
 * carro e o que só muda na OS é da API (`lib/os-vehicle.ts`); aqui só se diz o
 * que aconteceu — em especial quando a placa certa já era de um carro
 * cadastrado, e o modelo que aparece passa a ser o dele.
 */
export function OSVehicleFields({
  plate,
  make,
  model,
  yearModel,
  canEdit,
  onSave,
  isSaving,
}: {
  plate: string;
  make: string;
  model: string;
  yearModel: number;
  canEdit: boolean;
  onSave: (v: VehicleCorrection) => Promise<VehicleCorrection & { outcome: VehicleCorrectionOutcome }>;
  isSaving: boolean;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ plate: '', make: '', model: '', year: '' });
  const [catalogSearch, setCatalogSearch] = useState('');
  const [error, setError] = useState('');

  const start = () => {
    setDraft({ plate: maskPlate(plate), make, model, year: String(yearModel) });
    setCatalogSearch('');
    setError('');
    setEditing(true);
  };

  const pickCatalog = (item: CatalogItem) => {
    setDraft((d) => ({ ...d, make: item.make, model: item.model }));
    setCatalogSearch(`${item.make} ${item.model}`);
  };

  const save = async () => {
    setError('');
    // Só a placa NOVA precisa estar no padrão: cadastro antigo fora dele ainda corrige o modelo.
    const plateChanged = normalizePlate(draft.plate) !== normalizePlate(plate);
    if (plateChanged && !isValidPlate(draft.plate)) {
      setError('Placa inválida. Use o formato ABC-1234 (antiga) ou ABC1D23 (Mercosul).');
      return;
    }
    const year = Number(draft.year);
    if (!draft.make.trim() || !draft.model.trim() || !Number.isInteger(year) || year < 1900) {
      setError('Preencha marca, modelo e ano do veículo.');
      return;
    }
    try {
      const res = await onSave({ plate: normalizePlate(draft.plate), make: draft.make.trim(), model: draft.model.trim(), yearModel: year });
      setEditing(false);
      if (res.outcome === 'moved') {
        toast({
          type: 'success',
          title: `Placa corrigida para ${maskPlate(res.plate)}`,
          description: `Essa placa já estava cadastrada como ${res.make} ${res.model} ${res.yearModel}, e a OS passou para esse carro.`,
        });
      } else if (res.outcome === 'unchanged') {
        // Maiúscula ou espaço não contam como mudança: dizer, em vez de fechar
        // como se tivesse salvo.
        toast({ type: 'info', title: 'Nada mudou', description: 'O veículo já estava assim.' });
      } else {
        toast({ type: 'success', title: 'Veículo corrigido', description: 'A mudança fica no histórico da OS.' });
      }
    } catch (err) {
      // 409 do cadastro compartilhado: a mensagem da API diz o que dá para fazer.
      setError(formatApiError(err, 'Não foi possível corrigir o veículo. Tente novamente em instantes.'));
    }
  };

  if (!editing) {
    return (
      <>
        <div>
          <p className="text-xs text-muted-foreground">Placa</p>
          {canEdit ? (
            <button
              type="button"
              onClick={start}
              className="group flex items-center gap-1.5 text-left"
              aria-label={`Placa ${plate}. Corrigir a placa ou o modelo`}
            >
              <span className="font-mono font-semibold">{plate}</span>
              <Pencil size={12} className="text-muted-foreground group-hover:text-foreground" aria-hidden />
            </button>
          ) : (
            <p className="font-mono font-semibold">{plate}</p>
          )}
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Veículo</p>
          <p className="font-medium">
            {make} {model}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Ano</p>
          <p className="font-medium">{yearModel}</p>
        </div>
      </>
    );
  }

  return (
    <div className="col-span-2 space-y-3 rounded-md border p-3 sm:col-span-3">
      <p className="text-sm font-medium">Corrigir o veículo</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label htmlFor="fix-plate" className="text-xs text-muted-foreground">
            Placa
          </label>
          <input
            id="fix-plate"
            className={`${INPUT} font-mono uppercase`}
            value={draft.plate}
            onChange={(e) => setDraft((d) => ({ ...d, plate: maskPlate(e.target.value) }))}
            autoFocus
          />
        </div>
        <div>
          <label htmlFor="fix-year" className="text-xs text-muted-foreground">
            Ano modelo
          </label>
          <input
            id="fix-year"
            type="number"
            className={INPUT}
            value={draft.year}
            onChange={(e) => setDraft((d) => ({ ...d, year: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="fix-make" className="text-xs text-muted-foreground">
            Marca
          </label>
          <input
            id="fix-make"
            className={INPUT}
            value={draft.make}
            onChange={(e) => setDraft((d) => ({ ...d, make: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="fix-model" className="text-xs text-muted-foreground">
            Modelo
          </label>
          <input
            id="fix-model"
            className={INPUT}
            value={draft.model}
            onChange={(e) => setDraft((d) => ({ ...d, model: e.target.value }))}
          />
        </div>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Ou busque o modelo no catálogo</p>
        <div className="mt-1">
          <CatalogAutocomplete
            value={catalogSearch}
            onChangeText={setCatalogSearch}
            onSelect={pickCatalog}
            year={Number(draft.year) || undefined}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Se a placa certa já for de um carro cadastrado, a OS passa para ele, com o modelo de lá.
      </p>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={isSaving}
          className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={isSaving}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {isSaving && <Loader2 size={14} className="animate-spin" aria-hidden />}
          Salvar
        </button>
      </div>
    </div>
  );
}
